import { memo } from "react";
import { useTranslation } from "react-i18next";
import { FirestoreGenerationStatus } from "reedy-data/models";
import { Badge } from "~/components/ui/badge";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import {
  type BadgeStatus,
  getLabels,
  getTooltipLabel,
  statusColors,
} from "./labels";

interface GenerationStatusBadgeProps {
  status?: BadgeStatus;
  labelKey: "keywords" | "description" | "subjects" | "aPlusContent";
  errorMessage?: string;
}

export const GenerationStatusBadge = memo(
  ({
    status = FirestoreGenerationStatus.IMPORTED,
    labelKey,
    errorMessage,
  }: GenerationStatusBadgeProps) => {
    const { t } = useTranslation(["components"]);
    const colors = statusColors[status];
    const labels = getLabels(t, colors.icon);
    const isInProgress =
      status === FirestoreGenerationStatus.REQUESTED ||
      status === FirestoreGenerationStatus.GENERATING;

    const fieldLabel = labels.find((label) => label.key === labelKey);
    let statusLabel: (typeof labels)[number] | undefined;
    if (status === FirestoreGenerationStatus.GENERATING) {
      statusLabel = labels.find((label) => label.key === "generating");
    } else if (status === FirestoreGenerationStatus.REQUESTED) {
      statusLabel = labels.find((label) => label.key === "requested");
    }

    const badgeLabel = statusLabel ?? fieldLabel;

    if (!badgeLabel || !fieldLabel) {
      return null;
    }

    return (
      <Tooltip>
        <TooltipTrigger>
          <Badge
            data-testid={`badge-${labelKey}`}
            aria-label={`badge-${labelKey}-${status.toLowerCase()}`}
            className={`min-w-11 h-6 p-0 ${colors.bg} ${isInProgress ? "animate-pulse-3" : ""}`}
          >
            {badgeLabel.icon}
          </Badge>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="text-center shadow-tooltip text-secondary-600 whitespace-pre z-10 border-none"
          sideOffset={6}
          data-testid="badge-tooltip"
        >
          <TooltipArrow
            className="fill-white drop-shadow-xs"
            width={12}
            height={6}
          />
          <p>{errorMessage ?? getTooltipLabel(t, fieldLabel.label, status)}</p>
        </TooltipContent>
      </Tooltip>
    );
  },
);

GenerationStatusBadge.displayName = "GenerationStatusBadge";
